import { useFieldArray, useForm } from "react-hook-form";
import { Select } from "../../Select";
import { Input } from "../../Input";
import { ShipCard } from "../../../Cards/ShipCard";
import { ITEM_OPTIONS } from "../../../../data/items";
import { FormLabel } from "../../FormLabel";
import { ButtonGroup } from "../../../Buttons/ButtonGroup";
import { useShipContext } from "../../../../context/shipContext";
import {
  PREVIOUS_STEP,
  COMPLETE_ITEM_DETAILS_STEP,
} from "../../../../reducers/types";
import { useGetQuote } from "../../../../api/hooks";
import { createQuoteRequestForShipRequest } from "../../../../helpers";
import { useEffect, useMemo } from "react";
import { CustomsInvoiceForm } from "./Customs/CustomsInvoiceForm";
import { Alert } from "../../../Alerts/Alert";
import { yupResolver } from "@hookform/resolvers/yup";
import { getItemSchema } from "../../validations/ship";
import AdjustmentNotice from "../../../Alerts/AdjustmentNotice";
import { MeasurementSelect } from "../../MeasurementSelect";

export function ItemDetailsForm() {
  const { state: shipState, dispatch } = useShipContext();
  const { isLoading, isError, mutateAsync: getQuote, error } = useGetQuote();

  const fromCountry = shipState.from.isExistingAddress
    ? shipState.from?.existingAddress?.address.country.name
    : shipState.from?.country?.name;

  const isInternationalShipment = fromCountry !== shipState?.to?.country?.name;

  const itemSchema = useMemo(
    () => getItemSchema(isInternationalShipment),
    [isInternationalShipment]
  );

  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {
      ...shipState.item,
      type: shipState.item.type ? shipState.item.type : {}
    },
    resolver: yupResolver(itemSchema),
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "invoice",
  });

  const itemType = watch("type");
  const isImperial = watch("isImperial");
  const isPackage = itemType?.name === "Package";
  const onSubmit = async (data) => {
    const quoteRequest = createQuoteRequestForShipRequest(data, shipState);
    try {
      const res = await getQuote(quoteRequest);
      dispatch({
        type: COMPLETE_ITEM_DETAILS_STEP,
        payload: {
          item: data,
          rates: res.quotes,
        },
      });
    } catch (err) {}
  };

  const goBack = () => {
    dispatch({
      type: PREVIOUS_STEP,
    });
  };

  useEffect(() => {
    if (isInternationalShipment && fields.length === 0) {
      append();
    }
  }, [isInternationalShipment, fields.length, append]);

  return (
    <div data-testid="ship-items-step">
      <div className="mb-5">
        <AdjustmentNotice />
      </div>
      <ShipCard>
        {isError && (
          <div className="pb-5">
            <Alert code={error.response?.status} />
          </div>
        )}
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-10">
          <div className="sm:col-span-12 flex items-center">
            <div className="sm:col-span-3 mr-10">
              <FormLabel title={"Item Details"} />
            </div>

            {isPackage && (
              <div className="sm:col-span-3">
                <MeasurementSelect
                  control={control}
                  name="isImperial"
                  label="Units"
                />
              </div>
            )}
            {/* <div className="sm:col-span-6"> </div> */}
          </div>
          <div className={"sm:col-span-2"}>
            <Select
              options={ITEM_OPTIONS}
              label="Item type"
              control={control}
              name="type"
            />
          </div>
          {isPackage && (
            <>
              <div className="sm:col-span-2">
                <Input
                  label="Length"
                  control={control}
                  name="length"
                  addOnRight={isImperial ? "in" : "cm"}
                />
              </div>
              <div className="sm:col-span-2">
                <Input
                  label="Width"
                  control={control}
                  name="width"
                  addOnRight={isImperial ? "in" : "cm"}
                />
              </div>
              <div className="sm:col-span-2">
                <Input
                  label="Height"
                  control={control}
                  name="height"
                  addOnRight={isImperial ? "in" : "cm"}
                />
              </div>
              <div className="sm:col-span-3">
                <Input
                  label="Weight"
                  control={control}
                  name="weight"
                  addOnRight={isImperial ? "lbs" : "kg"}
                />
              </div>
              {/* {shouldShowInsuranceInputField && (
                <div className="sm:col-span-2">
                  <Input label="Unit Price" control={control} name="value" />
                </div>
              )}
              <div className="sm:col-span-12 pt-2">
                <Toggle
                  control={control}
                  name="insurance"
                  leftLabel="Insurance?"
                />
              </div> */}
            </>
          )}
        </div>
      </ShipCard>

      {isInternationalShipment && (
        <div className="mt-5">
          <CustomsInvoiceForm
            control={control}
            fields={fields}
            append={append}
            remove={remove}
            watch={watch}
            setValue={setValue}
          />
        </div>
      )}

      <div className="mt-5 sm:mt-5">
        <ButtonGroup
          primaryButtonOnClick={handleSubmit(onSubmit)}
          primaryButtonText="Continue"
          secondaryButtonOnClick={goBack}
          secondaryButtonText="Back"
          isLoading={isLoading}
          primaryTestId="items-continue"
          secondaryTestId="items-back"
        />
      </div>
    </div>
  );
}
